import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, ArrowUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { stats, services, processSteps, sectors } from '../mock';

const Section = ({ id, children, className = '' }) => (
  <section id={id} className={`px-6 lg:px-10 ${className}`}>
    <div className="max-w-[1400px] mx-auto">{children}</div>
  </section>
);

const SectionLabel = ({ children }) => (
  <div className="text-[11px] tracking-[0.3em] uppercase text-[#07301f]/70 font-medium mb-5">
    {children}
  </div>
);

export default function Home() {
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const el = document.getElementById(location.state.scrollTo);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <main>
      {/* HERO */}
      <section className="relative bg-[#07301f] text-white pt-[120px] pb-28 overflow-hidden">
        <div className="absolute inset-0 hero-grid-bg opacity-60" />
        <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-[#0a4a31] rounded-full blur-[120px] opacity-40" />
        <div className="corner-bracket bottom-10 left-10" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="fade-in-up text-[11px] tracking-[0.3em] uppercase text-white/70 font-medium mb-8">
              Comercio Internacional · Consultoría Estratégica
            </div>
            <h1 className="fade-in-up fade-in-up-delay-1 text-[44px] md:text-[64px] lg:text-[76px] leading-[1.02] font-bold tracking-tight mb-8">
              Estructuración estratégica para la expansión internacional de empresas.
            </h1>
            <p className="fade-in-up fade-in-up-delay-2 text-lg md:text-xl text-white/70 max-w-xl mb-10">
              Canalizamos oportunidades globales con enfoque técnico, regulatorio y financiero.
            </p>
            <div className="fade-in-up fade-in-up-delay-3 flex flex-wrap gap-4">
              <Link to="/solicitar">
                <Button className="bg-[#0a4a31] hover:bg-[#0d5a3c] text-white rounded-md h-14 px-7 text-[15px] font-medium group">
                  Solicitar Cotización
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a href="https://calendar.app.google/YUn8jvW6MYZFL6de8" target="_blank" rel="noreferrer">
                <Button variant="outline" className="border-white/80 bg-transparent text-white hover:bg-white hover:text-[#07301f] rounded-md h-14 px-7 text-[15px] font-medium">
                  Agendar Reunión
                </Button>
              </a>
            </div>

            {/* Insignia Agente Comercial Oficial CONSA */}
            <a
              href="https://www.grupoconsa.cl"
              target="_blank"
              rel="noreferrer"
              className="consa-badge fade-in-up fade-in-up-delay-4 mt-10 inline-flex items-center gap-4 pl-2 pr-6 py-2 rounded-full bg-white/5 border border-white/15 backdrop-blur-sm hover:bg-white/10 hover:border-white/30 transition-colors group"
            >
              <span className="shrink-0 w-12 h-12 rounded-full bg-white flex items-center justify-center overflow-hidden ring-1 ring-white/20">
                <img
                  src="https://customer-assets.emergentagent.com/job_lobby-contact-portal/artifacts/tpgm96l2_Logo%20CONSA%20PNG.png"
                  alt="CONSA"
                  className="w-10 h-10 object-contain"
                />
              </span>
              <span className="flex flex-col leading-tight">
                <span className="text-[10px] tracking-[0.28em] uppercase text-white/60 font-medium">
                  Agente Comercial Oficial
                </span>
                <span className="text-white text-sm font-semibold tracking-wide">
                  CONSA <span className="text-white/50 font-normal">· Consorcio Salinero del Tarapacá S.A.</span>
                </span>
              </span>
              <ArrowRight className="w-4 h-4 text-white/50 group-hover:text-white group-hover:translate-x-0.5 transition-all ml-1" />
            </a>
          </div>

          {/* Stats */}
          <div className="fade-in-up fade-in-up-delay-4 mt-24 pt-10 border-t border-white/15 grid grid-cols-1 md:grid-cols-3 gap-10 max-w-3xl">
            {stats.map((s) => (
              <div key={s.label}>
                <div className="text-5xl md:text-6xl font-bold tracking-tight">{s.value}</div>
                <div className="text-white/60 text-sm mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICIOS */}
      <Section id="servicios" className="py-28">
        <div className="max-w-2xl mb-16">
          <SectionLabel>Servicios</SectionLabel>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-[1.1] mb-6 text-[#0a0a0a]">
            Soluciones integrales para comercio internacional
          </h2>
          <p className="text-neutral-600 text-lg">
            Cada operación requiere un enfoque técnico especializado. Nuestros servicios cubren el ciclo completo de internacionalización.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {services.map((svc) => {
            const Icon = svc.icon;
            return (
              <div key={svc.id} className="service-card group bg-white border border-neutral-200 rounded-xl p-8 flex flex-col">
                <div className="flex items-start gap-5">
                  <div className="icon-circle w-14 h-14 rounded-full bg-neutral-100 text-[#07301f] flex items-center justify-center shrink-0">
                    <Icon className="w-6 h-6" strokeWidth={1.75} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#0a0a0a] mb-3">{svc.title}</h3>
                    <p className="text-neutral-600 text-[15px] leading-relaxed">{svc.description}</p>
                  </div>
                </div>
                <div className="mt-6 ml-[76px]">
                  <Link to={`/solicitar?servicio=${svc.id}`} className="inline-flex items-center gap-2 text-[#07301f] font-medium text-sm group/link">
                    Solicitar análisis
                    <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* PROCESO */}
      <Section id="proceso" className="py-28 bg-neutral-50">
        <div className="max-w-2xl mb-16">
          <SectionLabel>Proceso</SectionLabel>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-[1.1] mb-6 text-[#0a0a0a]">
            Cómo funciona
          </h2>
          <p className="text-neutral-600 text-lg">
            Un proceso estructurado para garantizar análisis rigurosos y propuestas de alto valor.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-10 relative">
          <div className="hidden md:block absolute top-8 left-[16%] right-[16%] h-px bg-neutral-200" />
          {processSteps.map((p, idx) => {
            const Icon = p.icon;
            return (
              <div key={idx} className="relative text-center">
                <div className="relative z-10 w-16 h-16 rounded-full bg-[#07301f] text-white flex items-center justify-center mx-auto mb-6">
                  <Icon className="w-6 h-6" strokeWidth={1.75} />
                </div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-[#07301f]/70 font-semibold mb-3">{p.step}</div>
                <h3 className="text-xl font-semibold text-[#0a0a0a] mb-3">{p.title}</h3>
                <p className="text-neutral-600 text-[15px] leading-relaxed max-w-xs mx-auto">{p.description}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* SECTORES */}
      <Section id="sectores" className="py-28">
        <div className="max-w-2xl mb-16">
          <SectionLabel>Sectores</SectionLabel>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-[1.1] mb-6 text-[#0a0a0a]">
            Sectores prioritarios
          </h2>
          <p className="text-neutral-600 text-lg">
            Experiencia especializada en los sectores con mayor potencial de internacionalización.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {sectors.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="sector-card bg-white border border-neutral-200 rounded-xl p-7 text-center">
                <div className="icon-circle w-14 h-14 rounded-full bg-neutral-100 text-[#07301f] flex items-center justify-center mx-auto mb-5">
                  <Icon className="w-6 h-6" strokeWidth={1.75} />
                </div>
                <h3 className="text-base font-semibold text-[#0a0a0a] mb-2">{s.title}</h3>
                <p className="text-neutral-500 text-sm leading-snug">{s.description}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* CTA */}
      <section className="relative bg-[#07301f] text-white py-28 overflow-hidden">
        <div className="absolute inset-0 hero-grid-bg opacity-40" />
        <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#0a4a31] rounded-full blur-[140px] opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-[1.1] mb-6 max-w-3xl mx-auto">
            ¿Está su empresa preparada para operar globalmente?
          </h2>
          <p className="text-white/70 text-lg max-w-xl mx-auto mb-10">
            Inicie el proceso con una solicitud estructurada. Nuestro equipo le contactará en un máximo de 48 horas.
          </p>
          <Link to="/solicitar">
            <Button variant="outline" className="border-white/80 bg-transparent text-white hover:bg-white hover:text-[#07301f] rounded-md h-14 px-7 text-[15px] font-medium group">
              Solicitar Cotización
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>

      <BackToTop />
    </main>
  );
}

function BackToTop() {
  const [visible, setVisible] = React.useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!visible) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="back-to-top fixed bottom-8 right-8 w-11 h-11 rounded-full bg-[#07301f] text-white flex items-center justify-center shadow-lg z-40"
      aria-label="Volver arriba"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
}
